import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { landingBlurBg } from '../../../assets/images';
import { GoogleIcon, LinkedInsmallIcon } from '../../../assets/icons';
import { AI_AUTH_COLORS } from './constants';
import { config } from '../../../config';
import { isAuthenticated } from '../../../routes/ProtectedRoute';
import { getAilaunchRedirectUrl } from '../../../utils/redirect.util';
import { ChevronRight } from 'lucide-react';

type SocialProvider = 'Google' | 'Linkedin';

const cardSx = {
  width: '100%',
  maxWidth: '500px',
  borderRadius: '20px',
  border: '1px solid rgba(16, 24, 40, 0.08)',
  boxShadow: '0 24px 64px rgba(16, 24, 40, 0.16)',
  px: { xs: 3.1, sm: 4.4 },
  py: { xs: 3.6, sm: 4.5 },
  // bgcolor: '#F9FAFB',
} as const;

const actionButtonSx = {
  justifyContent: 'space-between',
  textTransform: 'none',
  borderRadius: '10px',
  py: 1.05,
  px: 1.4,
  color: '#111827',
  borderColor: 'rgba(17, 24, 39, 0.14)',
  bgcolor: '#FFFFFF',
  '&:hover': {
    borderColor: 'rgba(17, 24, 39, 0.24)',
    bgcolor: '#FFFFFF',
  },
} as const;

export default function TryAiFlowSignupPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [searchParams] = useSearchParams();
  useEffect(() => {

    if (isAuthenticated()) {
      window.location.href = getAilaunchRedirectUrl();
    }
  }, []);
  const tryAiPromptFromUrl = useMemo(() => {
    const payloadParam = searchParams.get('payload');
    if (!payloadParam) return null;
    try {
      const payload = JSON.parse(decodeURIComponent(payloadParam)) as {
        try_ai_prompt?: string;
      };
      const p = payload.try_ai_prompt;
      return typeof p === 'string' && p.length > 0 ? p : null;
    } catch (_err: unknown) {
      return null;
    }
  }, [searchParams]);

  useEffect(() => {
    const payloadParam = searchParams.get('payload');
    if (!payloadParam) return;
    try {
      const decoded = decodeURIComponent(payloadParam);
      const payload = JSON.parse(decoded) as Record<string, unknown>;
      const try_ai_prompt = payload.try_ai_prompt;
      const webinarType = payload.webinarType;
      const createMode = payload.createMode;
      if (try_ai_prompt) {
        localStorage.setItem('try_ai_prompt', String(try_ai_prompt));
        localStorage.setItem('webinarType', String(webinarType));
        localStorage.setItem('createMode', String(createMode));
        console.log('AI Prompt stored:', try_ai_prompt);
      }
    } catch (_err: unknown) {
      // ignore malformed payload
    }
  }, [searchParams]);

  const tryAiPrompt =
    tryAiPromptFromUrl ?? (typeof window !== 'undefined' ? localStorage.getItem('try_ai_prompt') : null);
  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const isContinueDisabled = trimmedEmail.length === 0;

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
    },
    []
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!trimmedEmail) return;

      navigate(`/try-ai-flow/auth/create-password?email=${encodeURIComponent(trimmedEmail)}`);
    },
    [navigate, trimmedEmail]
  );

  const handleSocialSignIn = useCallback((provider: SocialProvider) => {
    const authUrl = config.sso_auth_url;
    // OAuth state: SsoCallback reads isRegistration + flow (token URL; signup must not add isLogin on AI redirect).
    const stateData = {
      isRegistration: true,
      flow: 'try-ai-flow',
      timestamp: Date.now(),
    };
    const params = new URLSearchParams({
      client_id: config.sso_client_id,
      response_type: 'code',
      scope: 'email openid profile phone',
      redirect_uri: `${window.location.origin}/auth/sso/callback`,
      state: JSON.stringify(stateData),
      identity_provider: provider,
      prompt: 'select_account',
    });
    window.location.href = `${authUrl}?${params.toString()}`;
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          backgroundImage: `url(${landingBlurBg})`,
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Link to="/try-ai-flow" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <svg width="190" height="32" viewBox="0 0 190 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.95104 0H28.3313C29.7369 0 31.0766 0.549074 32.065 1.53741C33.0533 2.52574 33.6024 3.86548 33.6024 5.27111V26.7289C33.6024 28.1345 33.0533 29.4743 32.065 30.4626C31.0766 31.4509 29.7369 32 28.3313 32H5.95104C4.54541 32 3.20567 31.4509 2.21734 30.4626C1.22901 29.4743 0.679932 28.1345 0.679932 26.7289V5.27111C0.679932 3.86548 1.22901 2.52574 2.21734 1.53741C3.20567 0.549074 4.54541 0 5.95104 0Z" fill="#22B060"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M25.0804 6.52295L29.8463 6.58884C29.8463 6.58884 22.9719 15.8792 29.9342 25.4989H25.0145C25.0145 25.4989 21.5004 22.468 21.4126 16.3404C21.4126 16.3404 21.3028 10.2786 25.0804 6.52295Z" fill="white"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M16.5599 6.52295L21.3258 6.58884C21.3258 6.58884 14.4295 15.8792 21.4137 25.4989H16.494C16.494 25.4989 12.9799 22.468 12.8921 16.3404C12.8701 16.3404 12.7603 10.2786 16.5599 6.52295Z" fill="white"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M8.01505 6.52295L12.781 6.58884C12.781 6.58884 5.88465 15.8792 12.8689 25.4989H7.9272C7.9272 25.4989 4.41313 22.468 4.32528 16.3404C4.32528 16.3404 4.21546 10.2786 8.01505 6.52295Z" fill="white"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M188.266 12.3433C187.958 12.1676 187.607 12.0578 187.233 12.0358C186.926 12.0578 186.64 12.1676 186.399 12.3433C186.157 12.519 185.96 12.7606 185.85 13.0461C185.367 14.298 185.191 15.6377 185.279 16.9775L185.301 17.6583V23.3248H181.809V9.33436H184.818V11.0914C185.103 10.4545 185.542 9.9054 186.113 9.51006C186.64 9.15866 187.255 8.96099 187.892 8.96099C188.398 8.98295 188.881 9.09277 189.32 9.3124L188.266 12.3433ZM176.647 21.8313C176.054 22.4243 175.352 22.9075 174.583 23.2369C173.88 23.5224 173.111 23.6762 172.343 23.6762C171.464 23.6762 170.586 23.5005 169.773 23.1271C168.96 22.7537 168.236 22.2266 167.664 21.5677C166.347 20.1621 165.622 18.2733 165.666 16.3405C165.6 14.3858 166.281 12.4751 167.599 11.0255C168.17 10.3666 168.894 9.83951 169.707 9.4881C170.52 9.13669 171.376 8.93903 172.255 8.96099C173.067 8.96099 173.88 9.11473 174.627 9.42221C175.395 9.77362 176.098 10.2568 176.669 10.8498V9.3124H180.183V23.3028H176.669L176.647 21.8313ZM175.703 13.3316C175.352 12.9582 174.912 12.6727 174.451 12.4751C173.968 12.2774 173.463 12.1895 172.958 12.2115C172.452 12.1895 171.947 12.2994 171.464 12.497C171.003 12.6947 170.564 13.0022 170.234 13.3755C169.509 14.1882 169.114 15.2424 169.158 16.3186C169.114 17.4167 169.509 18.4929 170.234 19.3055C170.586 19.6789 171.003 19.9864 171.464 20.1841C171.925 20.3817 172.43 20.4915 172.936 20.4696C173.441 20.4915 173.968 20.3817 174.429 20.1841C174.912 19.9864 175.33 19.6789 175.681 19.3055C176.428 18.4709 176.823 17.3948 176.757 16.2966C176.823 15.1985 176.428 14.1442 175.703 13.3316ZM160.461 17.1971C160.505 16.077 160.417 14.9569 160.241 13.8587C160.131 13.3536 159.846 12.9143 159.45 12.6068C159.055 12.2994 158.572 12.1676 158.067 12.1676C157.386 12.1456 156.727 12.4092 156.244 12.8704C155.695 13.3975 155.321 14.1003 155.19 14.8471C155.058 15.7915 155.014 16.7359 155.036 17.6803V23.3028H151.5V9.3124H155.036V10.74C155.673 10.169 156.419 9.68577 157.21 9.33436C157.847 9.07081 158.506 8.93903 159.187 8.93903C159.846 8.91706 160.505 9.04884 161.12 9.29043C161.734 9.53203 162.284 9.9054 162.745 10.3886C163.184 10.8937 163.513 11.4648 163.711 12.1017C163.931 12.7386 163.997 13.3975 163.953 14.0564V23.3028H160.483L160.461 17.1971ZM147.634 8.10444C147.327 8.10444 147.041 8.03854 146.778 7.92873C146.514 7.81891 146.251 7.64321 146.053 7.42358C145.833 7.20395 145.68 6.96236 145.548 6.67685C145.438 6.39133 145.372 6.08385 145.394 5.79833C145.394 5.20533 145.636 4.63429 146.053 4.21699C146.47 3.7997 147.041 3.55811 147.634 3.55811C147.92 3.55811 148.227 3.624 148.491 3.73381C148.754 3.84363 149.018 4.01933 149.216 4.23896C149.435 4.45859 149.589 4.70018 149.721 4.9857C149.831 5.27121 149.896 5.55673 149.875 5.86421C149.875 6.1717 149.831 6.45721 149.721 6.74273C149.611 7.02825 149.435 7.26985 149.216 7.46751C149.018 7.68714 148.776 7.84088 148.513 7.97266C148.227 8.06051 147.942 8.10444 147.634 8.10444ZM137.707 23.6762C136.938 23.6762 136.17 23.5444 135.445 23.2369C134.676 22.9075 133.973 22.4243 133.38 21.8313V23.3248H129.888V3.93148H133.38V10.8718C133.973 10.2568 134.676 9.77362 135.445 9.44418C136.192 9.1367 137.004 8.96099 137.817 8.98295C138.695 8.98295 139.574 9.15866 140.365 9.51006C141.177 9.86147 141.88 10.3886 142.473 11.0475C143.791 12.497 144.472 14.4078 144.406 16.3625C144.472 18.2952 143.747 20.1841 142.407 21.5897C141.814 22.2486 141.089 22.7757 140.277 23.149C139.464 23.5005 138.586 23.6762 137.707 23.6762ZM139.794 13.3536C139.442 12.9802 139.025 12.6727 138.564 12.4751C138.102 12.2774 137.575 12.1895 137.07 12.1895C136.565 12.1676 136.038 12.2774 135.577 12.4531C135.093 12.6508 134.676 12.9363 134.325 13.3096C133.951 13.705 133.666 14.1662 133.49 14.6714C133.292 15.1765 133.227 15.7256 133.249 16.2527C133.227 16.8018 133.292 17.3508 133.49 17.8779C133.688 18.4051 133.973 18.8663 134.347 19.2836C134.698 19.6569 135.115 19.9644 135.599 20.1621C136.082 20.3598 136.587 20.4476 137.092 20.4476C137.597 20.4476 138.102 20.3598 138.586 20.1621C139.069 19.9644 139.464 19.6569 139.815 19.2836C140.562 18.4709 140.958 17.3948 140.914 16.2966C140.936 15.2204 140.54 14.1442 139.794 13.3536ZM116.798 17.3289C116.93 18.2513 117.391 19.1079 118.094 19.7009C118.863 20.3158 119.829 20.6233 120.818 20.5794C121.454 20.5794 122.069 20.4696 122.662 20.228C123.255 19.9864 123.783 19.635 124.222 19.1957L127.187 20.5794C126.506 21.5677 125.605 22.3584 124.529 22.9075C123.387 23.4346 122.135 23.6981 120.861 23.6542C119.873 23.6981 118.885 23.5224 117.962 23.171C117.04 22.8196 116.205 22.2705 115.481 21.5677C114.8 20.8869 114.273 20.0742 113.899 19.1738C113.548 18.2733 113.372 17.3289 113.394 16.3625C113.372 15.3742 113.526 14.4078 113.899 13.4854C114.251 12.5629 114.8 11.7283 115.481 11.0475C116.139 10.3666 116.952 9.81755 117.853 9.44418C118.753 9.07081 119.697 8.8951 120.664 8.93903C121.652 8.8951 122.662 9.07081 123.585 9.42221C124.507 9.79558 125.364 10.3447 126.067 11.0475C126.77 11.7942 127.319 12.6727 127.67 13.6391C128.021 14.6055 128.175 15.6158 128.131 16.648V17.3069L116.798 17.3289ZM123.212 12.6727C122.509 12.1895 121.674 11.926 120.818 11.948C119.895 11.926 118.995 12.2115 118.248 12.7606C117.677 13.2438 117.238 13.8807 116.952 14.5835H124.573C124.354 13.7928 123.87 13.112 123.212 12.6727ZM105.795 23.3248L102.808 15.3522L99.7331 23.3248H97.5368L92.4854 9.33436H95.9335L98.7448 17.3069L101.776 9.33436H103.884L106.849 17.2191L109.682 9.33436H113.152L108.035 23.3248H105.795ZM149.369 23.3248H145.855V9.33436H149.369V23.3248Z" fill="#22B060"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M87.1699 28.4421H83.5241L86.4012 22.2705L81.0862 9.31235H84.6881L88.334 18.0975L92.3532 9.31235H95.9551L87.1699 28.4421ZM76.0787 14.1881L77.3745 14.825C78.4507 15.2863 79.417 15.9891 80.1638 16.8676C80.7568 17.7022 81.0203 18.7345 80.8885 19.7667C80.7787 20.799 80.2955 21.7434 79.5488 22.4242C78.5385 23.3027 77.2207 23.742 75.859 23.6761C74.9146 23.6981 73.9702 23.5004 73.1137 23.0831C72.2571 22.6658 71.5323 22.0509 70.9613 21.2822L73.1356 18.9321C73.531 19.4153 74.0361 19.8106 74.5852 20.0962C75.0244 20.3597 75.5516 20.5134 76.0567 20.5354C76.452 20.5574 76.8474 20.4476 77.1768 20.206C77.3086 20.1181 77.4184 20.0083 77.4843 19.8765C77.5502 19.7447 77.5941 19.591 77.6161 19.4373C77.6161 18.8882 77.1109 18.3611 76.0787 17.8559L74.8927 17.2629C72.6305 16.1209 71.4884 14.6933 71.4884 12.9802C71.4884 12.453 71.5982 11.904 71.8179 11.4208C72.0375 10.9376 72.3669 10.4983 72.7623 10.1469C73.6627 9.35627 74.8268 8.91701 76.0347 8.96094C76.9352 8.96094 77.8137 9.1586 78.6264 9.5759C79.417 9.97123 80.1198 10.5642 80.6469 11.289L78.4726 13.4633C77.8577 12.7386 77.0011 12.2554 76.0567 12.1456C75.7273 12.1236 75.3978 12.2115 75.1343 12.3872C75.0244 12.453 74.9366 12.5409 74.8927 12.6507C74.8268 12.7605 74.8048 12.8923 74.8048 13.0021C74.8048 13.1998 74.8927 13.3755 75.0244 13.5292C75.3539 13.7928 75.7053 14.0124 76.0787 14.1881ZM66.0855 21.8312C65.4925 22.4242 64.7897 22.9074 64.021 23.2369C63.3182 23.5224 62.5495 23.6761 61.7808 23.6761C60.9023 23.6761 60.0237 23.5004 59.2111 23.127C58.3985 22.7537 57.6737 22.2266 57.0807 21.5677C55.7629 20.162 55.0382 18.2732 55.104 16.3405C55.0382 14.3858 55.719 12.475 57.0148 11.0255C57.5859 10.3666 58.3106 9.83946 59.1233 9.48805C59.9359 9.13664 60.7925 8.93898 61.671 8.96094C62.4836 8.96094 63.2962 9.11468 64.043 9.42216C64.8117 9.75161 65.4925 10.2568 66.0855 10.8498V9.31235H69.5996V23.3027H66.0855V21.8312ZM65.1411 13.3316C64.7897 12.9582 64.3504 12.6727 63.8892 12.475C63.406 12.2773 62.9009 12.1895 62.3957 12.2115C61.8906 12.1895 61.3854 12.2993 60.9242 12.497C60.463 12.6946 60.0457 13.0021 59.6943 13.3755C58.9915 14.2101 58.6181 15.2643 58.6181 16.3405C58.6181 17.4167 59.0134 18.4709 59.7163 19.3055C60.0677 19.6789 60.485 19.9863 60.9462 20.184C61.4074 20.3817 61.9126 20.4915 62.4177 20.4695C62.9229 20.4915 63.45 20.3817 63.9112 20.184C64.3944 19.9863 64.8117 19.6789 65.1631 19.3055C65.9098 18.4929 66.3052 17.3947 66.2612 16.2966C66.2832 15.7475 66.1953 15.2204 66.0196 14.7152C65.8 14.1881 65.5145 13.7269 65.1411 13.3316ZM42.6511 17.3288C42.7828 18.2513 43.2441 19.1078 43.9688 19.7008C44.7375 20.3158 45.7039 20.6233 46.6703 20.5793C47.3072 20.5793 47.9222 20.4695 48.5152 20.2279C49.1082 19.9863 49.6353 19.6349 50.0745 19.1957L53.0395 20.5793C52.3587 21.5677 51.4582 22.3583 50.382 22.9074C49.2399 23.4345 47.9661 23.6981 46.7142 23.6541C45.7259 23.6981 44.7375 23.5224 43.8151 23.171C42.8927 22.8196 42.0581 22.2705 41.3333 21.5677C40.6524 20.8868 40.1034 20.0742 39.7519 19.1737C39.4005 18.2732 39.2248 17.3288 39.2468 16.3625C39.2248 15.3741 39.3786 14.4078 39.73 13.4853C40.0814 12.5629 40.6305 11.7283 41.3113 11.0255C41.9922 10.3226 42.7828 9.79553 43.6833 9.42216C44.5838 9.04879 45.5282 8.87309 46.4946 8.91701C47.4829 8.87309 48.4712 9.04879 49.4156 9.42216C50.3381 9.79553 51.1946 10.3446 51.8755 11.0474C52.5783 11.7942 53.1274 12.6727 53.4788 13.639C53.8302 14.6054 54.0059 15.6157 53.94 16.648L53.918 17.3069L42.6511 17.3288ZM49.0642 12.6727C48.3614 12.1895 47.5268 11.9259 46.6922 11.9479C45.7698 11.9259 44.8474 12.2115 44.1006 12.7605C43.5296 13.2437 43.0903 13.8806 42.8048 14.5835H50.4259C50.2063 13.7928 49.7231 13.1119 49.0642 12.6727Z" fill="black"></path>
          </svg>
        </Link>
        <Button
          component={Link}
          to="/try-ai-flow/auth/login"
          variant="contained"
          endIcon={<ChevronRight size={16} />}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            px: 1.9,
            py: 0.7,
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#111827',
            bgcolor: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#F9FAFB',
              boxShadow: 'none',
            },
          }}
        >
          Login
        </Button>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        <Paper elevation={0} sx={cardSx}>
          <Typography
            component="h1"
            variant="h2"
            sx={{
              textAlign: 'center',
              color: '#111827',
              fontSize: { xs: '2rem', sm: '2.2rem', md: '38px' },
              letterSpacing: '-0.01em',
              mb: 1.35,
            }}
          >
            Create your account
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.92rem',
              mb: { xs: '2.45rem', sm: '2.8rem' },
            }}
          >
            Your webinar funnel is just a step away
          </Typography>

          {tryAiPrompt && <Box
            sx={{
              borderRadius: '18px',
              border: '1px solid #E6E9EF',
              backgroundColor: '#F4F5F7',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
              px: { xs: 1.6, sm: 1.8 },
              py: { xs: 1.35, sm: 1.45 },
              mb: 2.5,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1.25 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "12px", sm: "14px" },
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  color: '#111827',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.45,
                }}
              >
                YOUR AI PROMPT IS SAVED
                <AutoAwesomeRoundedIcon sx={{ fontSize: 14, color: '#111827' }} />
              </Typography>
              <Box
                sx={{
                  borderRadius: '999px',
                  bgcolor: '#D5ECFC',
                  color: '#3B82F6',
                  fontWeight: 700,
                  fontSize: '12px',
                  px: '8px',
                  py: '3px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <CheckCircleOutlineRoundedIcon sx={{ fontSize: 12, color: '#3B82F6' }} />
                <Typography component="span" fontSize="inherit">Ready</Typography>
              </Box>
            </Stack>
            <Typography
              sx={{
                color: '#111827',
                fontSize: { xs: '12px', sm: '16px' },
                lineHeight: 1.22,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {tryAiPrompt}
            </Typography>
          </Box>}

          <Stack spacing={1.55}>
            <Button
              variant="outlined"
              onClick={() => handleSocialSignIn('Google')}
              sx={{
                ...actionButtonSx,
                transition: 'background 0.12s',
                '&:hover': {
                  bgcolor: '#F3F4F6', // light grey
                  borderColor: 'rgba(17, 24, 39, 0.16)',
                },
                // Target arrow icon for transition
                '& .arrow-animate': {
                  transition: 'transform 0.18s cubic-bezier(0.43,0.13,0.23,0.96)',
                },
                '&:hover .arrow-animate': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Stack direction="row" spacing={1.1} alignItems="center">
                <GoogleIcon />
                <Typography sx={{ fontSize: '0.89rem', fontWeight: 500 }}>
                  Continue with Google
                </Typography>
              </Stack>
              <ArrowForwardIosRoundedIcon
                className="arrow-animate"
                sx={{ fontSize: 12, color: '#6B7280', ml: 0.4 }}
              />
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleSocialSignIn('Linkedin')}
              sx={{
                ...actionButtonSx,
                transition: 'background 0.12s',
                '&:hover': {
                  bgcolor: '#F3F4F6', // light grey
                  borderColor: 'rgba(17, 24, 39, 0.16)',
                },
                '& .arrow-animate': {
                  transition: 'transform 0.18s cubic-bezier(0.43,0.13,0.23,0.96)',
                },
                '&:hover .arrow-animate': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Stack direction="row" spacing={1.1} alignItems="center">
                <LinkedInsmallIcon />
                <Typography sx={{ fontSize: '0.89rem', fontWeight: 500 }}>
                  Continue with LinkedIn
                </Typography>
              </Stack>
              <ArrowForwardIosRoundedIcon
                className="arrow-animate"
                sx={{ fontSize: 12, color: '#6B7280', ml: 0.4 }}
              />
            </Button>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2.7 }}>
            <Divider sx={{ flex: 1, borderColor: 'rgba(17, 24, 39, 0.12)' }} />
            <Typography sx={{ color: '#9CA3AF', fontWeight: 600, fontSize: '0.72rem' }}>
              OR
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(17, 24, 39, 0.12)' }} />
          </Stack>

          <Box component="form" onSubmit={handleSubmit} autoComplete="off">
            <TextField
              fullWidth
              value={email}
              onChange={handleEmailChange}
              placeholder="Use email address"
              variant="outlined"
              size="medium"
              inputProps={{ autoComplete: 'off' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: 'rgba(17, 24, 39, 0.14)' },
                  '&:hover fieldset': { borderColor: 'rgba(17, 24, 39, 0.22)' },
                },
                '& input': {
                  fontSize: '0.9rem',
                  color: '#111827',
                  py: 1.1,
                },
                '& input::placeholder': {
                  color: '#9CA3AF',
                  opacity: 1,
                },
                mb: 2.15,
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isContinueDisabled}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.28,
                fontSize: '0.92rem',
                fontWeight: 600,
                color: '#FFFFFF',
                bgcolor: AI_AUTH_COLORS.primaryMain,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: AI_AUTH_COLORS.primaryHover,
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: AI_AUTH_COLORS.primaryDisabled,
                  color: '#FFFFFF',
                },
              }}
            >
              Continue
            </Button>
          </Box>

          <Divider sx={{ my: 2.7, borderColor: 'rgba(17, 24, 39, 0.12)' }} />
          <Typography sx={{ textAlign: 'center', color: '#4B5563', fontSize: '0.9rem' }}>
            Already have an account?{' '}
            <Typography
              component={Link}
              to="/try-ai-flow/auth/login"
              sx={{
                display: 'inline',
                color: AI_AUTH_COLORS.primary,
                textDecoration: 'none',
                fontSize: 'inherit',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign in here
            </Typography>
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}
